/**
 * @module index
 * @package @geenius/adapters/react
 * @description Defines the React adapter hooks for reading adapter instances
 * and adapter status state from the React provider context. These hooks are
 * part of the public React runtime package and avoid unpublished shared
 * framework subpaths.
 */

import type {
  AdapterDomain,
  AdapterStatusInfo,
  AdminAdapter,
  AiAdapter,
  AuthAdapter,
  DbAdapter,
  FileStorageAdapter,
  LoggerAdapter,
  PaymentsAdapter,
} from "@geenius/adapters";
import { useMemo } from "react";
import { useAdapterContext } from "../provider";

/**
 * Reads the configured database adapter from the current React adapter context.
 *
 * @returns The active database adapter implementation for the provider tree.
 * @throws {AdapterContextError} When no React adapter provider is mounted.
 *
 * @example
 * ```tsx
 * function Users() {
 *   const db = useDb()
 *   return null
 * }
 * ```
 */
// @doc
export function useDb(): DbAdapter {
  return useAdapterContext().getAdapter("db");
}

/**
 * Reads the configured authentication adapter from the current React context.
 *
 * @returns The active authentication adapter implementation.
 * @throws {AdapterContextError} When the auth adapter is unavailable.
 */
// @doc
export function useAuth(): AuthAdapter {
  return useAdapterContext().getAdapter("auth");
}

/**
 * Reads the configured authentication adapter from the current React context.
 *
 * @returns The active authentication adapter implementation.
 * @throws {AdapterContextError} When the auth adapter is unavailable.
 */
// @doc
export function useAuthAdapter(): AuthAdapter {
  return useAuth();
}

/**
 * Reads the configured payments adapter from the current React context.
 *
 * @returns The active payments adapter implementation.
 * @throws {AdapterContextError} When the payments adapter is unavailable.
 */
// @doc
export function usePayments(): PaymentsAdapter {
  return useAdapterContext().getAdapter("payments");
}

/**
 * Reads the configured AI adapter from the current React context.
 *
 * @returns The active AI adapter implementation.
 * @throws {AdapterContextError} When the AI adapter is unavailable.
 */
// @doc
export function useAi(): AiAdapter {
  return useAdapterContext().getAdapter("ai");
}

/**
 * Reads the configured file-storage adapter from the current React context.
 *
 * @returns The active file-storage adapter implementation.
 * @throws {AdapterContextError} When the storage adapter is unavailable.
 */
// @doc
export function useFileStorage(): FileStorageAdapter {
  return useAdapterContext().getAdapter("storage");
}

/**
 * Reads the configured file-storage adapter from the current React context.
 *
 * @returns The active file-storage adapter implementation.
 * @throws {AdapterContextError} When the storage adapter is unavailable.
 */
// @doc
export function useStorage(): FileStorageAdapter {
  return useFileStorage();
}

/**
 * Reads the configured admin adapter from the current React context.
 *
 * @returns The active admin adapter implementation.
 * @throws {AdapterContextError} When the admin adapter is unavailable.
 */
// @doc
export function useAdmin(): AdminAdapter {
  return useAdapterContext().getAdapter("admin");
}

/**
 * Reads the configured logger adapter from the current React context.
 *
 * @returns The active logger adapter implementation.
 * @throws {AdapterContextError} When the logger adapter is unavailable.
 */
// @doc
export function useLogger(): LoggerAdapter {
  return useAdapterContext().getAdapter("logger");
}

/**
 * Returns the full adapter status record from the current React context.
 *
 * @returns A status object keyed by adapter domain.
 */
// @doc
export function useAdapterStatuses(): Record<AdapterDomain, AdapterStatusInfo> {
  return useAdapterContext().statuses;
}

/**
 * Returns the current status for a single adapter domain.
 *
 * @param domain Adapter domain whose status should be returned.
 * @returns The latest status snapshot for the requested domain.
 */
// @doc
export function useAdapterStatus(domain: AdapterDomain): AdapterStatusInfo {
  return useAdapterContext().statuses[domain];
}

/**
 * Reports whether a specific adapter domain is configured and connected.
 *
 * @param domain Adapter domain to inspect.
 * @returns `true` when the domain is ready for consumer use.
 */
// @doc
export function useIsAdapterReady(domain: AdapterDomain): boolean {
  return useAdapterContext().isReady(domain);
}

/**
 * Accesses every adapter and status helper from the current provider context.
 *
 * @returns A memoized snapshot of adapters, statuses, and loading helpers.
 */
// @doc
export function useAdapters(): {
  db: DbAdapter;
  auth: AuthAdapter;
  payments: PaymentsAdapter;
  ai: AiAdapter;
  fileStorage: FileStorageAdapter;
  storage: FileStorageAdapter;
  admin: AdminAdapter;
  logger: LoggerAdapter;
  statuses: Record<AdapterDomain, AdapterStatusInfo>;
  isLoading: boolean;
  isReady: (domain: AdapterDomain) => boolean;
} {
  const context = useAdapterContext();

  return useMemo(
    () => ({
      db: context.adapters.db,
      auth: context.adapters.auth,
      payments: context.adapters.payments,
      ai: context.adapters.ai,
      fileStorage: context.adapters.fileStorage,
      storage: context.adapters.storage,
      admin: context.adapters.admin,
      logger: context.adapters.logger,
      statuses: context.statuses,
      isLoading: context.isLoading,
      isReady: context.isReady,
    }),
    [context],
  );
}
